import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchRSSFeed } from '../services/rssService';
import { RSSFeed, FeedOptions, RSSItem } from '../types/rss';
import indexedDBService from '../services/indexedDBService';

const DEFAULT_OPTIONS: FeedOptions = {
  layout: 'card',
  theme: 'dark',
  showImages: true,
  sortBy: 'newest',
  refreshInterval: 5, // 5 minutos
  rssUrl: 'https://feeds.folha.uol.com.br/emcimadahora/rss091.xml',
  // Customization defaults
  fontSize: 16,
  fontColor: '#000000',
  backgroundColor: '#ffffff',
  customTheme: false,
  tickerSpeed: 1.5 // velocidade atual (1.5x mais rápido)
};

export const useRSSFeed = () => {
  const [feed, setFeed] = useState<RSSFeed | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [options, setOptions] = useState<FeedOptions>(DEFAULT_OPTIONS);
  const [isBackgroundRefreshing, setIsBackgroundRefreshing] = useState<boolean>(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<number>(Date.now());
  const [newItemsDetected, setNewItemsDetected] = useState<number>(0);
  const lastFeedHashRef = useRef<string>('');
  const quickCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Função para buscar o feed com cache IndexedDB
  const loadFeed = useCallback(async (isBackgroundUpdate = false) => {
    try {
      if (!isBackgroundUpdate) {
        // Só mostra loading se não há feed carregado ainda
        if (!feed) {
          setLoading(true);
        }
      } else {
        setIsBackgroundRefreshing(true);
      }
      setError(null);
      
      // Inicializa o IndexedDB se necessário
      await indexedDBService.init();
      
      const feedData = await fetchRSSFeed(options.rssUrl);
      
      // Ordena os itens conforme a opção selecionada
      const sortedItems = sortItems(feedData.items, options.sortBy);
      
      // Para atualizações em background, verifica se há novas notícias
      if (isBackgroundUpdate && feed) {
        const currentItemIds = new Set(feed.items.map(item => item.link));
        const newItems = sortedItems.filter(item => !currentItemIds.has(item.link));
        
        if (newItems.length > 0) {
          // Adiciona novas notícias ao início da lista (para 'newest') ou final (para 'oldest')
          const updatedItems = options.sortBy === 'newest' 
            ? [...newItems, ...feed.items]
            : [...feed.items, ...newItems];
          
          setFeed({ ...feedData, items: updatedItems });
          setNewItemsDetected(prev => prev + newItems.length);
          setLastUpdateTime(Date.now());
          
          console.log(`🆕 ${newItems.length} nova(s) notícia(s) adicionada(s) em tempo real`);
          
          // Notifica sobre as novas notícias
          if (typeof window !== 'undefined' && 'Notification' in window) {
            if (Notification.permission === 'granted') {
              new Notification(`${newItems.length} nova(s) notícia(s)`, {
                body: newItems[0]?.title || 'Novas notícias disponíveis',
                icon: '/favicon.ico',
                tag: 'rss-update'
              });
            }
          }
        } else {
          console.log('📊 Verificação completa - nenhuma nova notícia');
        }
      } else {
        setFeed({ ...feedData, items: sortedItems });
      }
      
      // Cache cleanup é feito separadamente para evitar loops
    } catch (err) {
      if (!isBackgroundUpdate) {
        setError('Erro ao carregar o feed RSS. Por favor, tente novamente mais tarde.');
      }
      console.error('Erro ao carregar feed:', err);
    } finally {
      if (!isBackgroundUpdate) {
        setLoading(false);
      } else {
        setIsBackgroundRefreshing(false);
      }
    }
  }, [options.rssUrl, options.sortBy, feed]);

  // Função para verificação rápida de mudanças no feed
  const quickFeedCheck = useCallback(async () => {
    if (!options.rssUrl) return;
    
    try {
      // Fazer uma requisição HEAD para verificar mudanças sem baixar o conteúdo
      const response = await fetch(options.rssUrl, { 
        method: 'HEAD',
        cache: 'no-cache'
      });
      
      if (response.ok) {
        const lastModified = response.headers.get('last-modified');
        const etag = response.headers.get('etag');
        const contentLength = response.headers.get('content-length');
        
        // Criar um hash simples baseado nos cabeçalhos
        const currentHash = `${lastModified}-${etag}-${contentLength}`;
        
        if (lastFeedHashRef.current && lastFeedHashRef.current !== currentHash) {
          console.log('🔍 Mudanças detectadas via quick check, atualizando feed...');
          lastFeedHashRef.current = currentHash;
          await loadFeed(true);
        } else if (!lastFeedHashRef.current) {
          lastFeedHashRef.current = currentHash;
        }
      }
    } catch (error) {
      console.log('⚠️ Quick check falhou, fazendo verificação completa...');
      await loadFeed(true);
    }
  }, [options.rssUrl, loadFeed]);

  // Ordena os itens do feed
  const sortItems = (items: RSSItem[], sortBy: 'newest' | 'oldest'): RSSItem[] => {
    return [...items].sort((a, b) => {
      const dateA = new Date(a.pubDate).getTime();
      const dateB = new Date(b.pubDate).getTime();
      return sortBy === 'newest' ? dateB - dateA : dateA - dateB;
    });
  };

  // Atualiza as opções do feed
  const updateOptions = (newOptions: Partial<FeedOptions>) => {
    const updatedOptions = { ...options, ...newOptions };
    setOptions(updatedOptions);
    
    // Se a opção de ordenação foi alterada, reordena os itens
    if (newOptions.sortBy && feed) {
      const sortedItems = sortItems(feed.items, updatedOptions.sortBy);
      setFeed({ ...feed, items: sortedItems });
    }
    
    // Salva as opções no localStorage
    localStorage.setItem('rss-feed-options', JSON.stringify(updatedOptions));
  };

  // Carrega as opções salvas no localStorage
  useEffect(() => {
    const savedOptions = localStorage.getItem('rss-feed-options');
    if (savedOptions) {
      try {
        const parsedOptions = JSON.parse(savedOptions);
        setOptions({ ...DEFAULT_OPTIONS, ...parsedOptions });
      } catch (err) {
        console.error('Erro ao carregar opções salvas:', err);
      }
    }
  }, []);

  // Limpa cache expirado apenas uma vez quando o componente é montado
  useEffect(() => {
    indexedDBService.clearExpiredFeeds().catch(err => 
      console.warn('Erro ao limpar cache expirado:', err)
    );
  }, []);

  // Carrega o feed quando o componente é montado ou quando o URL muda
  useEffect(() => {
    loadFeed();
  }, [loadFeed]);
  
  // Sistema de detecção em tempo real com verificações rápidas
  useEffect(() => {
    if (!feed || options.refreshInterval <= 0) return;
    
    // Verificação rápida a cada 30 segundos
    const quickCheckInterval = setInterval(() => {
      quickFeedCheck();
    }, 30 * 1000);
    
    // Verificação completa no intervalo configurado (fallback)
    const fullCheckInterval = setInterval(() => {
      console.log('🔄 Verificação completa programada');
      loadFeed(true);
    }, options.refreshInterval * 60 * 1000);
    
    quickCheckIntervalRef.current = quickCheckInterval;
    
    return () => {
      clearInterval(quickCheckInterval);
      clearInterval(fullCheckInterval);
      if (quickCheckIntervalRef.current) {
        clearInterval(quickCheckIntervalRef.current);
      }
    };
  }, [feed, options.refreshInterval, loadFeed, quickFeedCheck]);
  
  // Solicita permissão para notificações na primeira carga
  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission().then(permission => {
          if (permission === 'granted') {
            console.log('✅ Notificações habilitadas para novas notícias');
          }
        });
      }
    }
  }, []);

  // Limpa recursos do IndexedDB quando o componente é desmontado
  useEffect(() => {
    return () => {
      // Cleanup será feito automaticamente pelo garbage collector
    };
  }, []);

  return {
    feed,
    loading,
    error,
    options,
    updateOptions,
    refreshFeed: () => loadFeed(false),
    isBackgroundRefreshing,
    lastUpdateTime,
    newItemsDetected,
    forceQuickCheck: quickFeedCheck
  };
};